import { IconRegistry } from '@tylertech/forge';
import {
  ForgeBadge,
  ForgeButton,
  ForgeCard,
  ForgeIcon,
  ForgeLabelValue,
  ForgeList,
  ForgeListItem,
  ForgeSkeleton,
  ForgeToolbar
} from '@tylertech/forge-react';
import { tylIconCellphone, tylIconMail } from '@tylertech/tyler-icons';
import { useEffect, useRef, useState } from 'react';
import ChangePasswordDialog from '../components/dialogs/ChangePasswordDialog';
import AuthenticatorSetupDialog from '../components/dialogs/AuthenticatorSetupDialog';
import ConfirmActionDialog from '../components/dialogs/ConfirmActionDialog';
import EnableSmsDialog from '../components/dialogs/EnableSmsDialog';
import DisableSmsDialog from '../components/dialogs/DisableSmsDialog';
import { removeMfaFactor } from '../api/mfa';
import useProfile from '../hooks/useProfile';
import useMfaFactors from '../hooks/useMfaFactors';
import { setTablePresentationRole } from '../utils/forgePatches';
import './Security.css';

const maskPhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/[^\d]/g, '');
  if (digits.length < 4) return phone;
  return `••••••${digits.slice(-4)}`;
};

export default function Security() {
  IconRegistry.define([tylIconCellphone, tylIconMail]);

  const { profile, isLoading: isLoadingProfile, error: profileError } = useProfile();
  const { smsFactor, isSmsMfaEnabled, totpFactor, isTotpEnabled, isLoading: isLoadingMfa, error: mfaQueryError, refetch: refetchMfa } = useMfaFactors();

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState('');
  const [showEnableSms, setShowEnableSms] = useState(false);
  const [showDisableSms, setShowDisableSms] = useState(false);
  const [smsStatus, setSmsStatus] = useState('');
  const [showAuthenticatorDialog, setShowAuthenticatorDialog] = useState(false);
  const [showDisableAuthenticator, setShowDisableAuthenticator] = useState(false);
  const [authenticatorStatus, setAuthenticatorStatus] = useState('');
  const [authenticatorError, setAuthenticatorError] = useState('');
  const [isRemovingAuthenticator, setIsRemovingAuthenticator] = useState(false);
  const rootRef = useRef(null);

  const email = profile?.email || '';
  const emailError = profileError ? 'Unable to load your email right now.' : '';
  const emailDisplay = isLoadingProfile ? (
    <ForgeSkeleton style={{ width: '200px', height: '1rem', display: 'inline-block' }} ariaLabel="Loading email address" />
  ) : email || 'Not available';

  const handleOpenAuthenticatorDialog = () => {
    setAuthenticatorError('');
    setAuthenticatorStatus('');
    setShowAuthenticatorDialog(true);
  };

  const handleAuthenticatorSuccess = () => {
    setAuthenticatorStatus('Authenticator app set up successfully.');
    setShowAuthenticatorDialog(false);
  };

  const handleConfirmRemoveAuthenticator = () => {
    setAuthenticatorStatus('');
    setAuthenticatorError('');
    setShowDisableAuthenticator(true);
  };

  const handleRemoveAuthenticator = async () => {
    if (isRemovingAuthenticator) return;

    setIsRemovingAuthenticator(true);
    setAuthenticatorError('');
    try {
      const factorId = totpFactor?.id || totpFactor?.factorId;
      if (!factorId) {
        throw new Error('No authenticator factor found to remove');
      }
      await removeMfaFactor(factorId);
      await refetchMfa();
      setAuthenticatorStatus('Authenticator app has been disabled.');
    } catch {
      setAuthenticatorError('Unable to remove the authenticator right now. Please try again.');
    } finally {
      setIsRemovingAuthenticator(false);
      setShowDisableAuthenticator(false);
    }
  };

  useEffect(() => {
    setTablePresentationRole(rootRef.current);
  }, []);

  return (
    <div className="security" ref={rootRef}>
      <section className="flex-col gap-4 section-padding section-border-bottom">
        <h3 className="forge-typography--subheading3 section-header">Change Password</h3>
        {passwordStatus ? <p className="dialog-status dialog-status--success">{passwordStatus}</p> : null}
        <button
          type="button"
          className="security__change-password-btn"
          onClick={() => setShowChangePassword(true)}>
          Change Password
        </button>
      </section>

      <section className="flex-col gap-4 section-padding section-border-bottom">
        <h3 className="forge-typography--subheading3 section-header">Multi-factor Authentication</h3>
        <p>
          Manage your multi-factor authentication (MFA) methods. Depending on what you have already set up, you can either enable a new method or replace an
          existing one.
        </p>
        <div className="mfa-cards gap-6 mt-2">
          <ForgeCard className="tonal-card card--white">
            <ForgeToolbar className="text-color--medium">
              <ForgeIcon slot="start" className="mr-6" name="mail" />
              <h4 slot="start" className="forge-typography--subheading2 text--bold">
                Email
              </h4>
              <ForgeBadge slot="end" theme="success">
                Enabled
              </ForgeBadge>
            </ForgeToolbar>
            <p className="forge-typography--body1 m-6">Verification codes will be sent to your email address that can be used to sign into your account.</p>
            {emailError ? <p className="info-card__error mx-6">{emailError}</p> : null}
            <ForgeLabelValue className="mx-6 mb-6">
              <span slot="label">Email Address</span>
              <span slot="value" className="text-color--medium">
                {emailDisplay}
              </span>
            </ForgeLabelValue>
          </ForgeCard>

          <ForgeCard className="tonal-card card--white">
            <ForgeToolbar className="text-color--medium">
              <ForgeIcon slot="start" className="mr-6" name="cellphone" />
              <h4 slot="start" className="forge-typography--subheading2 text--bold">
                SMS / Text Message
              </h4>
              {isLoadingMfa ? (
                <ForgeSkeleton slot="end" style={{ width: '80px', height: '1.25rem' }} ariaLabel="Loading SMS status" />
              ) : (
                <ForgeBadge slot="end" theme={isSmsMfaEnabled ? 'success' : 'secondary'}>
                  {isSmsMfaEnabled ? 'Enabled' : 'Not Enabled'}
                </ForgeBadge>
              )}
            </ForgeToolbar>
            <p className="forge-typography--body1 m-6">
              Verification codes will be sent to your phone via text message that can be used to sign into your account.
            </p>
            {smsStatus ? <p className="dialog-status dialog-status--success mx-6">{smsStatus}</p> : null}
            {isSmsMfaEnabled ? (
              <>
                <ForgeLabelValue className="mx-6 mb-6">
                  <span slot="label">SMS Phone Number</span>
                  <span slot="value" className="text-color--medium">
                    {maskPhone(smsFactor?.phoneNumber)}
                  </span>
                </ForgeLabelValue>
                <div className="flex gap-4 mx-6 mb-6">
                  <ForgeButton variant="outlined" onClick={() => setShowEnableSms(true)}>
                    Change Number
                  </ForgeButton>
                  <ForgeButton variant="outlined" onClick={() => setShowDisableSms(true)}>
                    Disable
                  </ForgeButton>
                </div>
              </>
            ) : (
              <div className="mx-6 mb-6">
                <ForgeButton variant="outlined" onClick={() => setShowEnableSms(true)} disabled={isLoadingMfa}>
                  Enable
                </ForgeButton>
              </div>
            )}
          </ForgeCard>
        </div>

        <ForgeCard className="tonal-card card--white mfa-cards__authenticator mt-6">
          <ForgeToolbar className="text-color--medium">
            <ForgeIcon slot="start" className="mr-6" name="cellphone" />
            <h4 slot="start" className="forge-typography--subheading2 text--bold">
              Authenticator Apps
            </h4>
            {isLoadingMfa ? (
              <ForgeSkeleton slot="end" style={{ width: '80px', height: '1.25rem', display: 'inline-block' }} ariaLabel="Loading MFA status" />
            ) : (
              <ForgeBadge slot="end" theme={isTotpEnabled ? 'success' : 'secondary'}>
                {isTotpEnabled ? 'Enabled' : 'Not Enabled'}
              </ForgeBadge>
            )}
          </ForgeToolbar>
          <p className="forge-typography--body1 m-6">
            Verification codes can be generated using an authenticator app to sign in to your account.
          </p>
          <p className="forge-typography--body1 m-6">
            Examples of authenticator apps currently supported by MySC.gov are: Google Authenticator, Authy, LastPass, 1Password, and OTP Manager.
          </p>
          {mfaQueryError ? <p className="dialog-status dialog-status--error mx-6">Unable to load MFA status right now.</p> : null}
          {authenticatorError ? <p className="dialog-status dialog-status--error mx-6">{authenticatorError}</p> : null}
          {authenticatorStatus ? <p className="dialog-status dialog-status--success mx-6">{authenticatorStatus}</p> : null}
          <ForgeList className="mfa-card__list my-2">
            <ForgeListItem>
              <span className="text-color--medium forge-typography--subheading1">
                Authenticator App
              </span>
              {isLoadingMfa ? (
                <ForgeSkeleton slot="end" style={{ width: '80px', height: '2.25rem', display: 'inline-block' }} ariaLabel="Loading authenticator status" />
              ) : (
                <ForgeButton
                  slot="end"
                  variant="outlined"
                  onClick={isTotpEnabled ? handleConfirmRemoveAuthenticator : handleOpenAuthenticatorDialog}
                  disabled={isLoadingMfa || isRemovingAuthenticator || Boolean(mfaQueryError)}>
                  {isTotpEnabled ? 'Disable' : 'Enable'}
                </ForgeButton>
              )}
            </ForgeListItem>
          </ForgeList>
        </ForgeCard>
      </section>

      <ChangePasswordDialog
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onSave={() => {
          setShowChangePassword(false);
          setPasswordStatus('Password updated successfully.');
        }}
      />

      <EnableSmsDialog
        open={showEnableSms}
        onClose={() => setShowEnableSms(false)}
        onComplete={() => {
          setSmsStatus('SMS authentication has been successfully enabled.');
        }}
        existingFactorId={isSmsMfaEnabled ? (smsFactor?.id || smsFactor?.factorId) : null}
      />

      <DisableSmsDialog
        open={showDisableSms}
        factorId={smsFactor?.id || smsFactor?.factorId}
        onClose={() => setShowDisableSms(false)}
        onComplete={() => {
          setSmsStatus('SMS authentication has been disabled.');
        }}
      />

      <AuthenticatorSetupDialog
        open={showAuthenticatorDialog}
        onClose={() => setShowAuthenticatorDialog(false)}
        onComplete={handleAuthenticatorSuccess}
      />

      <ConfirmActionDialog
        open={showDisableAuthenticator}
        title="Disable Authenticator App"
        message="Are you sure you want to disable the authenticator app on your account? You will no longer be able to use it to sign in."
        confirmText={isRemovingAuthenticator ? 'Disabling...' : 'Disable'}
        onClose={() => setShowDisableAuthenticator(false)}
        onConfirm={handleRemoveAuthenticator}
        isSubmitting={isRemovingAuthenticator}
      />
    </div>
  );
}
